import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/base/MovieCard";
import MovieGridSkeleton from "@/components/base/MovieGridSkeleton";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import FilterBar from "@/pages/category/components/FilterBar";
import { CATEGORIES, REGIONS, SORTS, YEARS } from "@/mocks/site";
import { getMoviesByType, movies, searchMovies, TYPE_DESCRIPTIONS, TYPE_LABELS } from "@/lib/media";
import type { MovieType } from "@/lib/types";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { type = "all" } = useParams();
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get("q") ?? "").trim();

  const [region, setRegion] = useState(REGIONS[0]);
  const [year, setYear] = useState(YEARS[0]);
  const [sort, setSort] = useState(SORTS[0].key);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const category = CATEGORIES.find((item) => item.key === type);
  const isValidType = Boolean(category) || type === "all";
  const label = type === "all" ? "全部作品" : category ? category.label : "未知分类";
  const typeLabel = type === "all" ? "影视作品" : TYPE_LABELS[type as MovieType] ?? "影视作品";

  usePageMeta({
    title: `${label}${keyword ? ` · ${keyword}` : ""} - 华丽的外出影视大全`,
    description: category
      ? `${TYPE_DESCRIPTIONS[category.key as MovieType]} 按地区与年份筛选，快速找到想看的${category.label}。`
      : "华丽的外出全部影视作品合集，涵盖电影、电视剧、综艺与动画，支持地区、年份筛选与热度排序。",
    keywords: `华丽的外出,${label},${typeLabel}剧情介绍,${typeLabel}推荐,影视筛选`,
  });

  useEffect(() => {
    setPage(1);
  }, [type, keyword, region, year, sort]);

  useEffect(() => {
    setRegion(REGIONS[0]);
    setYear(YEARS[0]);
    setSort(SORTS[0].key);
  }, [type]);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 240);
    return () => window.clearTimeout(timer);
  }, [type, keyword, region, year, sort, page]);

  const filtered = useMemo(() => {
    let base = type === "all" ? [...movies] : getMoviesByType(type);
    if (keyword) {
      const matchedIds = new Set(searchMovies(keyword).map((movie) => movie.id));
      base = base.filter((movie) => matchedIds.has(movie.id));
    }
    if (region !== REGIONS[0]) base = base.filter((movie) => movie.region === region);
    if (year !== YEARS[0]) base = base.filter((movie) => String(movie.year) === year);

    if (sort === "popular") base.sort((a, b) => b.popularity - a.popularity);
    else if (sort === "rating") base.sort((a, b) => b.rating - a.rating);
    else base.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));

    return base;
  }, [type, keyword, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setRegion(REGIONS[0]);
    setYear(YEARS[0]);
    setSort(SORTS[0].key);
  };

  const changePage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!isValidType) {
    return (
      <SiteLayout>
        <div className="shell py-16">
          <EmptyState
            icon="ri-folder-unknow-line"
            title="这个分类还不存在"
            desc="我们只提供电影、电视剧、综艺与动画四个分类，你可以回到全部作品继续浏览。"
            actionTo="/category/all"
            actionLabel="浏览全部作品"
          />
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="shell pt-5 md:pt-7">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-300" />
          <span className="text-foreground-800">{label}</span>
          {keyword && (
            <>
              <i className="ri-arrow-right-s-line text-sm text-foreground-300" />
              <span className="text-foreground-800">搜索「{keyword}」</span>
            </>
          )}
        </nav>

        <header className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-secondary-50 px-5 py-6 md:px-8 md:py-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-3 w-1 rounded-full bg-accent-500" />
                <span className="font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-accent-700">
                  {type === "all" ? "All Titles" : category?.en}
                </span>
              </div>
              <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
                {keyword ? `「${keyword}」的搜索结果` : category ? `${label}专区` : "全部影视作品"}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {keyword
                  ? `在${typeLabel}中为你找到 ${filtered.length} 部相关作品，也可以调整下方筛选条件缩小范围。`
                  : category
                    ? TYPE_DESCRIPTIONS[category.key as MovieType]
                    : "电影、电视剧、综艺与动画，全部作品在这里汇合。"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {CATEGORIES.map((item) => (
                <Link
                  key={item.key}
                  to={`/category/${item.key}`}
                  className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                    item.key === type
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </header>

        <div className="mt-5">
          <FilterBar
            region={region}
            year={year}
            sort={sort}
            count={filtered.length}
            onChange={(patch) => {
              if (patch.region !== undefined) setRegion(patch.region);
              if (patch.year !== undefined) setYear(patch.year);
              if (patch.sort !== undefined) setSort(patch.sort);
            }}
            onReset={resetFilters}
          />
        </div>

        <div className="mt-6">
          {loading ? (
            <MovieGridSkeleton count={PAGE_SIZE} />
          ) : pageItems.length === 0 ? (
            <EmptyState
              icon="ri-search-eye-line"
              title="没有找到匹配的作品"
              desc="换个关键词，或者重置筛选条件试试看。我们收录的作品还在持续增加中。"
              action={
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line text-sm" />
                  重置筛选
                </button>
              }
              actionTo="/"
              actionLabel="回到首页"
            />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} showOverview />
                ))}
              </div>
              <Pagination page={currentPage} totalPages={totalPages} onChange={changePage} />
            </>
          )}
        </div>

        {keyword && (
          <div className="mt-6 flex flex-wrap items-center gap-2 rounded-md border border-background-200 bg-background-100/70 px-4 py-3 text-xs text-foreground-600">
            <i className="ri-search-line text-sm text-foreground-400" />
            搜索范围包含片名、导演、演员、地区与题材标签。
            <Link to="/category/all" className="font-medium text-primary-700 hover:underline">
              清除搜索
            </Link>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}